import { getAuth } from 'firebase/auth';

const BASE = import.meta.env.VITE_API_URL;

async function authHeaders(): Promise<HeadersInit> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export const contactRequestsApi = {
  create: async (payload: {
    dealId: string;
    message?: string;
    partnerIds?: string[];
  }) => {
    const res = await fetch(`${BASE}/contact-requests`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  accept: async (requestId: string) => {
    const res = await fetch(
      `${BASE}/contact-requests/${requestId}/accept`,
      { method: 'PATCH', headers: await authHeaders() },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  decline: async (requestId: string) => {
    const res = await fetch(
      `${BASE}/contact-requests/${requestId}/decline`,
      { method: 'PATCH', headers: await authHeaders() },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  getAsSeller: async () => {
    const res = await fetch(
      `${BASE}/contact-requests/as-seller`,
      { headers: await authHeaders() },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  getAsBroker: async () => {
    const res = await fetch(
      `${BASE}/contact-requests/as-broker`,
      { headers: await authHeaders() },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  getAsBuyer: async () => {
    const res = await fetch(
      `${BASE}/contact-requests/as-buyer`,
      { headers: await authHeaders() },
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // ── Backward-compat aliases (legacy callers) ──────────
  getAll: async () => contactRequestsApi.getAsBuyer(),
  getMyRequests: async () => contactRequestsApi.getAsBuyer(),
  getReceived: async () => contactRequestsApi.getAsSeller(),
  reject: async (requestId: string, _reason?: string) =>
    contactRequestsApi.decline(requestId),
  getByDeal: async (dealId: string) => {
    const all = await contactRequestsApi.getAsBuyer();
    return Array.isArray(all) ? all.filter((r: any) => r.dealId === dealId) : [];
  },
};

// ── Backward-compat types ────────────────────────────────
export type ContactRequest = {
  id: string;
  dealId: string;
  dealName?: string;
  buyerId: string;
  sellerId?: string;
  brokerId?: string;
  buyerProfile?: Record<string, any>;
  partnerIds?: string[];
  partnerProfiles?: Record<string, any>[];
  buyerMessage?: string;
  status: 'pending' | 'accepted' | 'declined' | 'rejected';
  jitsiRoom?: string;
  createdAt?: any;
  updatedAt?: any;
  acceptedAt?: any;
  declinedAt?: any;
  [key: string]: any;
};

export type CreateContactRequestDto = {
  dealId: string;
  message?: string;
  partnerIds?: string[];
};
